import {noop} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {useEventHandler} from '../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {FrontClickEvent} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DataEventActionsEnum} from '../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {InfoBubbleInstanceProps} from '../../../libs/shared/ui/src/components/tip/infoBubble';
import {InfoBubblePillVariantsEnum} from '../../../libs/shared/ui/src/components/tip/infoBubblePill';
import heroImage from '../../assets/images/personalWorkspaceTip.svg';
import {useAppSelector} from '../../helpers/appReduxHelpers';
import {OnboardingTipTypesEnum} from '../../models/api/preferencesModel';
import {getHasSandbox} from '../../store/data/teams/teamsSelectors';
import {EducationTip} from '../educationTips/educationTip';
import {generateStepText} from '../onboarding/welcome/onboardingWelcomeHelpers';
import {useOnboardingInfoBubblePresenter} from './onboardingInfoBubbleHook';

/*
 * Styles.
 */

const StyledImg = styled.img`
  display: block;
  width: 380px;
  height: 142px;
`;

/*
 * Component.
 */

export const TeammateWorkspaceInfoBubble: FC<InfoBubbleInstanceProps> = ({anchor}) => {
  const {isVisible, closeInfoBubble: onClose} = useOnboardingInfoBubblePresenter(
    OnboardingTipTypesEnum.TEAMMATE_WORKSPACE,
  );
  const hasUserSandboxAccess = useAppSelector(getHasSandbox);
  const totalSteps = hasUserSandboxAccess ? 3 : 2;

  const {trackDataEvent} = useDataEventTracking();
  const onPrimaryClick = useEventHandler((_event: FrontClickEvent) => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.ONBOARDING_TEAMMATE_WORKSPACE_NEXT, {
      has_sandbox_access: hasUserSandboxAccess,
    });
    onClose();
  });

  if (!isVisible) {
    return null;
  }

  return (
    <EducationTip
      anchor={anchor}
      position="right-start"
      isInteractive
      isVisible={isVisible}
      onRequestClose={noop}
      hero={<StyledImg src={heroImage} alt="Teammate workspace illustration" />}
      pillType={InfoBubblePillVariantsEnum.EDUCATION}
      title={<Trans id="infobubble-teammateworkspace-title">Welcome to your workspace!</Trans>}
      primaryActionLabel={<Trans id="infobubble-teammateworkspace-cta">Next</Trans>}
      onPrimaryClick={onPrimaryClick}
      stepCounter={generateStepText(1, totalSteps)}
      dataObjectName={OnboardingDataEventObject.ONBOARDING_TEAMMATE_WORKSPACE}
    >
      <Trans id="infobubble-teammateworkspace-description">All of your messages live here.</Trans>
    </EducationTip>
  );
};
